import React, { useState, useRef, useEffect } from 'react';

export interface SwimlaneRowProps {
  readonly swimlaneId: string;
  readonly title: string;
  readonly color: string;
  readonly startDate?: string | null;
  readonly endDate?: string | null;
  readonly collapsed: boolean;
  readonly onToggleCollapse: (swimlaneId: string) => void;
  readonly onRename?: (swimlaneId: string, newTitle: string) => void;
  readonly onArchive?: (swimlaneId: string) => void;
  readonly onMoveUp?: (swimlaneId: string) => void;
  readonly onMoveDown?: (swimlaneId: string) => void;
  readonly onSetSprintDates?: (swimlaneId: string, startDate: string | null, endDate: string | null) => void;
  readonly children?: React.ReactNode;
}

/**
 * Swimlane row — renders a horizontal swimlane section with header, collapse toggle,
 * inline rename, and archive action via ⋮ menu.
 */
export function SwimlaneRow({
  swimlaneId,
  title,
  color,
  startDate,
  endDate,
  collapsed,
  onToggleCollapse,
  onRename,
  onArchive,
  onMoveUp,
  onMoveDown,
  onSetSprintDates,
  children,
}: SwimlaneRowProps): React.ReactElement {
  const headerStyle = color ? { borderLeftColor: color, borderLeftWidth: '4px' } : undefined;
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const [showDateDialog, setShowDateDialog] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const handleSubmitRename = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== title && onRename) {
      onRename(swimlaneId, trimmed);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSubmitRename();
    if (e.key === 'Escape') { setEditValue(title); setIsEditing(false); }
  };

  return (
    <section
      role="region"
      aria-label={`Swimlane: ${title}`}
      className="mb-4"
    >
      <div
        data-swimlane-header
        className="flex items-center gap-2 px-3 py-2 bg-card rounded-t border-l-4 border-border"
        style={headerStyle}
      >
        <button
          onClick={() => onToggleCollapse(swimlaneId)}
          aria-label={`${collapsed ? 'Expand' : 'Collapse'} swimlane: ${title}`}
          aria-expanded={!collapsed}
          className="text-muted-foreground hover:text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring rounded p-1 transition-colors"
        >
          <span aria-hidden="true" className="inline-block transition-transform">
            {collapsed ? '▶' : '▼'}
          </span>
        </button>

        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSubmitRename}
            onKeyDown={handleKeyDown}
            className="text-xs font-semibold uppercase tracking-wide bg-transparent border-b border-primary text-card-foreground focus:outline-none px-0 py-0"
            aria-label="Rename swimlane"
          />
        ) : (
          <h3
            className="text-xs font-semibold uppercase tracking-wide text-muted-foreground cursor-pointer hover:text-card-foreground"
            onDoubleClick={() => { if (onRename) { setEditValue(title); setIsEditing(true); } }}
            title={onRename ? 'Double-click to rename' : undefined}
          >
            {title}
          </h3>
        )}

        {startDate && endDate && (
          <span
            className="text-[11px] text-muted-foreground"
            aria-label={`Sprint from ${startDate} to ${endDate}`}
          >
            {formatSprintDateRange(startDate, endDate)}
            {isSprintActive(startDate, endDate) && (
              <span className="ml-1.5 text-green-500 font-medium">Active</span>
            )}
          </span>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Menu */}
        {(onRename || onArchive || onMoveUp || onMoveDown || onSetSprintDates) && (
          <SwimlaneMenu
            title={title}
            onRename={onRename ? () => { setEditValue(title); setIsEditing(true); } : undefined}
            onArchive={onArchive ? () => onArchive(swimlaneId) : undefined}
            onMoveUp={onMoveUp ? () => onMoveUp(swimlaneId) : undefined}
            onMoveDown={onMoveDown ? () => onMoveDown(swimlaneId) : undefined}
            onSprintDates={onSetSprintDates ? () => setShowDateDialog(true) : undefined}
          />
        )}
      </div>

      {!collapsed && (
        <div className="flex gap-3 overflow-x-auto p-3 bg-card border border-t-0 border-border rounded-b">
          {children}
        </div>
      )}

      {/* Sprint Dates Dialog */}
      {showDateDialog && (
        <SprintDateDialog
          startDate={startDate ?? null}
          endDate={endDate ?? null}
          onSave={(start, end) => {
            if (onSetSprintDates) onSetSprintDates(swimlaneId, start, end);
            setShowDateDialog(false);
          }}
          onClose={() => setShowDateDialog(false)}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sprint Date Helpers
// ---------------------------------------------------------------------------

function formatSprintDateRange(startDate: string, endDate: string): string {
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const startStr = start.toLocaleDateString('en-US', opts);
  const endStr = end.toLocaleDateString('en-US', opts);
  return `${startStr} – ${endStr}`;
}

function isSprintActive(startDate: string, endDate: string): boolean {
  const today = new Date().toISOString().slice(0, 10);
  return today >= startDate && today <= endDate;
}

// ---------------------------------------------------------------------------
// SwimlaneMenu
// ---------------------------------------------------------------------------

interface SwimlaneMenuProps {
  readonly title: string;
  readonly onRename?: () => void;
  readonly onArchive?: () => void;
  readonly onMoveUp?: () => void;
  readonly onMoveDown?: () => void;
  readonly onSprintDates?: () => void;
}

function SwimlaneMenu({ title, onRename, onArchive, onMoveUp, onMoveDown, onSprintDates }: SwimlaneMenuProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); buttonRef.current?.focus(); }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={buttonRef}
        onClick={() => setOpen(!open)}
        className="text-muted-foreground hover:text-card-foreground p-1 rounded hover:bg-muted/50 text-xs"
        aria-label={`Swimlane actions for ${title}`}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        ⋮
      </button>

      {open && (
        <div
          role="menu"
          aria-label={`Actions for ${title}`}
          className="absolute right-0 top-full mt-1 z-50 min-w-[140px] rounded-md border border-border bg-popover shadow-md py-1"
        >
          {onRename && (
            <button
              role="menuitem"
              onClick={() => { onRename(); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-sm text-popover-foreground hover:bg-muted focus:bg-muted focus:outline-none"
            >
              Rename
            </button>
          )}
          {onMoveUp && (
            <button
              role="menuitem"
              onClick={() => { onMoveUp(); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-sm text-popover-foreground hover:bg-muted focus:bg-muted focus:outline-none"
            >
              Move up
            </button>
          )}
          {onMoveDown && (
            <button
              role="menuitem"
              onClick={() => { onMoveDown(); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-sm text-popover-foreground hover:bg-muted focus:bg-muted focus:outline-none"
            >
              Move down
            </button>
          )}
          {onSprintDates && (
            <button
              role="menuitem"
              onClick={() => { onSprintDates(); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-sm text-popover-foreground hover:bg-muted focus:bg-muted focus:outline-none"
            >
              Sprint dates
            </button>
          )}
          {(onRename || onMoveUp || onMoveDown || onSprintDates) && onArchive && (
            <div className="my-1 border-t border-border" role="separator" />
          )}
          {onArchive && (
            <button
              role="menuitem"
              onClick={() => { onArchive(); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10 focus:bg-destructive/10 focus:outline-none"
            >
              Archive swimlane
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SprintDateDialog
// ---------------------------------------------------------------------------

interface SprintDateDialogProps {
  readonly startDate: string | null;
  readonly endDate: string | null;
  readonly onSave: (startDate: string | null, endDate: string | null) => void;
  readonly onClose: () => void;
}

function SprintDateDialog({ startDate, endDate, onSave, onClose }: SprintDateDialogProps): React.ReactElement {
  const [start, setStart] = useState(startDate ?? '');
  const [end, setEnd] = useState(endDate ?? '');
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSave = () => {
    const startVal = start || null;
    const endVal = end || null;

    if (startVal && endVal && endVal <= startVal) {
      setError('End date must be after start date');
      return;
    }

    setError(null);
    onSave(startVal, endVal);
  };

  const handleClear = () => {
    onSave(null, null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-label="Set sprint dates"
        className="bg-popover border border-border rounded-lg shadow-lg p-4 w-80"
        onClick={(e) => e.stopPropagation()}
      >
        <h4 className="text-sm font-semibold text-foreground mb-3">Sprint Dates</h4>

        <div className="space-y-3">
          <div>
            <label htmlFor="sprint-start" className="block text-xs font-medium text-muted-foreground mb-1">
              Start Date
            </label>
            <input
              id="sprint-start"
              type="date"
              value={start}
              onChange={(e) => { setStart(e.target.value); setError(null); }}
              className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div>
            <label htmlFor="sprint-end" className="block text-xs font-medium text-muted-foreground mb-1">
              End Date
            </label>
            <input
              id="sprint-end"
              type="date"
              value={end}
              onChange={(e) => { setEnd(e.target.value); setError(null); }}
              className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {error && (
            <p className="text-xs text-destructive" role="alert">{error}</p>
          )}
        </div>

        <div className="flex items-center justify-between mt-4">
          <button
            type="button"
            onClick={handleClear}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Clear dates
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
